import { useCallback } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FeatureFlag } from 'api/generated/types/featureFlag';
import { type FeatureFlagsResponseFeatureFlags } from 'api/generated/types/featureFlagsResponseFeatureFlags';
import { getFeatureFlags } from 'api/generated/api/feature-flags';
import { extractErrorMessage } from 'utils/notify';
import { getValidOrgId, getValidUserId } from 'utils/storageUtils';
import { registerSessionReset } from 'stores/sessionResetRegistry';

// Re-export FeatureFlag for convenience
export { FeatureFlag };

/**
 * Whether `flag` is on in a feature-flag payload. Components that need a callable
 * checker should use `useFeatureFlagChecker`, since the store's `isFeatureEnabled`
 * reads `get()` and its identity never changes.
 */
function isFlagEnabled(
  featureFlags: FeatureFlagsResponseFeatureFlags | null,
  flag: FeatureFlag
): boolean {
  if (!featureFlags) return false;
  return featureFlags[flag] ?? false;
}

interface FeatureFlagsState {
  // State
  featureFlags: FeatureFlagsResponseFeatureFlags | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;

  // Actions
  fetchFeatureFlags: (force?: boolean) => Promise<void>;
  refreshFeatureFlags: () => Promise<void>;
  clearFeatureFlags: () => void;

  // Feature flag checkers
  isFeatureEnabled: (flag: FeatureFlag) => boolean;
}

// Bumped by clearFeatureFlags so a fetch started before logout cannot land the
// previous org's flags after it.
let featureFlagsGeneration = 0;

export const useFeatureFlagsStore = create<FeatureFlagsState>()(
  devtools(
    (set, get) => ({
      featureFlags: null,
      isLoading: false,
      isLoaded: false,
      error: null,

      fetchFeatureFlags: async (force = false) => {
        const state = get();

        // If already loaded and not forcing, do nothing
        if (state.isLoaded && !force) {
          return;
        }

        const userId = getValidUserId();
        const orgId = getValidOrgId();

        if (!userId || !orgId) {
          console.warn(
            'fetchFeatureFlags: Invalid userId or orgId, skipping API call'
          );
          return;
        }

        const generation = featureFlagsGeneration;
        set({ isLoading: true, error: null });

        try {
          const { featureFlagsGetFeatureFlags } = getFeatureFlags();
          const response = await featureFlagsGetFeatureFlags();
          if (generation !== featureFlagsGeneration) {
            return;
          }

          set({
            featureFlags: response.feature_flags,
            isLoaded: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          if (generation !== featureFlagsGeneration) {
            return;
          }
          console.error('Failed to fetch feature flags:', error);
          const errorMessage = extractErrorMessage(
            error,
            // i18n-ok: stored as a failure flag, never rendered
            'Failed to fetch feature flags'
          );
          set({ error: errorMessage, isLoading: false });
        }
      },

      refreshFeatureFlags: async () => {
        await get().fetchFeatureFlags(true);
      },

      clearFeatureFlags: () => {
        featureFlagsGeneration += 1;
        set({
          featureFlags: null,
          isLoading: false,
          isLoaded: false,
          error: null,
        });
      },

      isFeatureEnabled: (flag: FeatureFlag) =>
        isFlagEnabled(get().featureFlags, flag),
    }),
    { name: 'feature-flags-store' }
  )
);

registerSessionReset(() => useFeatureFlagsStore.getState().clearFeatureFlags());

/**
 * A `(flag) => boolean` checker whose identity changes whenever the flag payload
 * does, so memos that list it in their deps recompute on a flag refresh.
 */
export function useFeatureFlagChecker(): (flag: FeatureFlag) => boolean {
  const featureFlags = useFeatureFlagsStore((state) => state.featureFlags);
  return useCallback(
    (flag: FeatureFlag) => isFlagEnabled(featureFlags, flag),
    [featureFlags]
  );
}

// Initialize feature flags - call this from App.js
export const initializeFeatureFlags = () => {
  const userId = getValidUserId();
  const orgId = getValidOrgId();

  if (userId && orgId) {
    void useFeatureFlagsStore.getState().fetchFeatureFlags(true);
  }
};
